import React, {Component} from 'react';
import { useCallback, useRef } from 'react';
import { connect } from "react-redux";
import { Container, Row, Col } from 'react-bootstrap'
import Alert from '../common/modal/Alert';
import axios from "axios";
import configData from "../../config/config.json";
import usStates from "../../config/us_states.json";
import translations from "../../config/translation/efile_translations.json";
import FormTemplate from "../common/form/FormTemplate";
import { LOADING_STATUES } from "../constants/constants";
import CommonTop from "../top/CommonTop";
import EfileConfirmation from "./EfileConfirmation";
import CreditCardEfilePayment from "./CreditCardEfilePayment";
import moment from 'moment';
import AjaxResponseHandler from "../../utils/AjaxResponseHandler"
import AjaxResult from "../../utils/AjaxResult";
import {backendLogger} from "../../logger.js"
import { validateUser } from "../common/function/userValidation.js";
import { computeSelectValue } from "../common/function/formUtils.js"; 
import SkipLink from '../common/function/SkipLink.js';
import { Button, Form, FormFile } from 'react-bootstrap'

const componentName = "efile";

const mapStateToProps = state => {
  return {
    countyStateCode: state.productPropertiesReducer.countyStateCode,
    language: state.currentUserReducer.language ? state.currentUserReducer.language : 'ENGLISH',
    countyConfigCountyEfile: state.countyConfigCountyEfileReducer,
    currentUser: state.currentUserReducer
  };
}

const matchDispatchToProps = (dispatch) => {
  return {
    handleLockout: (lockout) =>  dispatch({ type: 'SET_LOCKOUT_IN_PLACE', lockout }),
    updateUsageBalance: (usageBalance) =>  dispatch({ type: 'UPDATE_CURRENT_USER_USAGE_BALANCE', usageBalance })
  }
}

const PAYMENT_METHOD_CREDIT_CARD = 'CREDIT_CARD';
    
/**
 * An Efile submission.
 * The controller expects a 'primaryImage' and 0 or more 'helperImage_<int>.'
 * 
 * After a sprint review we decided on the following:
 * - 'helper' is a TriMin name, 'additional info' will be the user facing name.
 * - Due to the amount of work involved in LL/LH we will restrict the number of
 *   helpers to 0 or 1, though the server code handles more and the UI is built
 *   for more.  Use 'maxHelperImageCount' to limit the count.
 */
const maxHelperImageCount = 1;    // For infinite, set to a very large number

class Efile extends Component {
  constructor(props) {
    super(props);

    this.state = {
      fileKey: moment(),
      numberOfPages: 0,
      autoFillChecked: false,
      name: '',
      email: '',
      city: '',
      state: this.props.countyStateCode ? this.props.countyStateCode : 'MP',
      zip: '',
      address1: '',
      address2: '',
      submitMessage: '',
      paymentMethod: '',
      transactionNumber: '',
      imageId: '',
      showConfirmationDialog: false,
      loadingStatus: '',
      firstPageCharge: '',
      additionalPagesCharge: '',
      surcharge: '',
      totalCharge: '',
      showCharges: false,

      userName: '',
      userAddress: '',
      userCity: '',
      userState: '',
      userZip: '',
      userEmail: '',

      showAlert: false,
      alertMessage: "",
      efileSubmitPending: false,

      acknowledgement: false,
      docType: computeSelectValue(props.currentUser.efileDocTypes),
      errors: {},
      primaryImage: null,
      helperImages: [],  // file(html file), id(int)
      nextHelperImageKeyIdx: 1
    }
    
    this.helperImageInputRefs = {};
  }

  getHelperImageName = (id) => { return 'helperImage_' + id; }
  getHelperImageRemoveName = (id) => { return 'helperImageRemove_' + id; }

  // Returns the helper image or null;
  // Text is an element ID from an event handler.
  getHelperImage = (text) => {
    if (!text) return null;

    let parts = text.split('_');

    if (parts.length != 2) return null;

    let id = +(parts[1]);  // converts to int
    let entry = this.state.helperImages.find( (item) => item.id === id );
    return entry ?? null;
  }

  validate = () => {
    // Validate the images.
    let fileErrors = {};

    this.validateFile(true, this.state.primaryImage, 'primaryImage', fileErrors);

    this.state.helperImages.forEach((item, idx) => {
      if (!item.removed) {
        this.validateFile(false, item.file, item.id, fileErrors);
      }
    });

		if (fileErrors && Object.keys(fileErrors).length > 0) {
			this.setState({ errors: fileErrors });
			return;
		}

    let errors = validateUser(this.state, false, false);
		if (errors && Object.keys(errors).length > 0) {
			this.setState({ errors });
			return;
		}

    if (this.props.countyConfigCountyEfile.acknowledgement === true && !this.state.acknowledgement) {
      this.setState(
        { errors: { 'acknowledgement': 'Please read and select the checkbox to acknowledge.'} }
      );
      return false;
    }

    return true;
  }

  validateFile = (isPrimary, stateFile, formName, errors) => {
      if (!stateFile) {
        if (isPrimary) {
          errors[formName] = translations[this.props.language].required;
        } else {
          errors[formName] = translations[this.props.language].helper_document_missing_error;
        }
        return false;
      }

      const allowedFormats = new Set(["application/pdf", "image/tiff"]);
      const megabyte = 1000 * 1000;
      const maxSize = 25 * megabyte;

      if (!allowedFormats.has(stateFile.type)) {
        errors[formName] = translations[this.props.language].invalid_file_format;
        return false;
      }

      if (stateFile.size > maxSize) {
        errors[formName] = translations[this.props.language].exceeds_max_size;
        return false;
      }

      return true;
  }

  handleSubmit = (event) => {
    event.preventDefault();
    if (!this.validate()) {
      return false;
    }

    const formData = new FormData();

    let metadata = {
            docType: this.state.docType,
            numberOfPages: this.state.numberOfPages,
            name: this.state.name,
            email: this.state.email,
            city: this.state.city,
            state: this.state.state,
            zip: this.state.zip,
            address1: this.state.address1,
            address2: this.state.address2,

            transactionNumber: this.state.transactionNumber,
            imageId: this.state.imageId,

            firstPageCharge: this.state.firstPageCharge,
            additionalPagesCharge: this.state.additionalPagesCharge,
            surcharge: this.state.surcharge,
            totalCharge: this.state.totalCharge,
            showCharges: this.state.showCharges
      };
      
    formData.append(
      'metadata',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' })
    );

    formData.append("primaryImage", this.state.primaryImage);

      this.state.helperImages.forEach((item) => {
          if (item.file && !item.removed) {
            formData.append(this.getHelperImageName(item.id), item.file);
          }
      });

      if (this.showLoading() === true) {
        axios({ method: "post", url: configData.LANDSHARK_SERVER_URL + "efile/image", data: formData, headers: { "Content-Type": "multipart/form-data" }, withCredentials: true })
          .then((response) => {
            let content = response.data.content;
            
            this.setState(
              {
                numberOfPages: content.numberOfPages,
                firstPageCharge: content.firstPageCharge,
                additionalPagesCharge: content.additionalPagesCharge,
                paymentMethod: content.paymentMethod,
                surcharge: content.surcharge,
                totalCharge: content.totalCharge,
                showCharges: content.showCharges,
                imageId: content.imageId,
                loadingStatus: ""
              }, this.showConfirmation);
          }, (error) => {
            this.setState({ loadingStatus: "" }, function() {
              AjaxResponseHandler.handleError(error, this.showAlert, this.props.history);
            }); 
       });
      }
  }

  componentDidMount() {
		axios.get(configData.LANDSHARK_SERVER_URL + "currentUser/profile", { withCredentials: true })
		.then(
			(response) => {
				const content = response.data.content;
				this.setState( {
               userName: content.firstName + ' ' + content.lastName,
               userAddress: content.address,
               userCity: content.city,
               userState: content.state,
               userZip: content.zip,
               userEmail: content.email
				});
			},
			(error) => {
			   console.log(error);
			}
		);
	}

  showLoading = () => {
    if (this.state.loadingStatus === "") {
      this.setState({
        loadingStatus: LOADING_STATUES.LOADING
      })
      return true;
    } 
    return false;
  }

  hideLoading = (message, status) => {
    if (status === 'ERROR') {
      alert(message);
      this.setState({
        loadingStatus: "",
        submitMessage: ""
      })
    } else {
      this.setState({
        loadingStatus: "",
        submitMessage: message
      })
    }
  }

  handleClear = (event) => {
    if (event) {
      event.preventDefault();
    }

    // Clear out the helper files.
    this.helperImageInputRefs = [];
    this.state.helperImages.forEach((item) => {
      item.file = null;
      item.removed = true;
    });

    this.setState((prevState, props) => ({
      fileKey: moment(),
      docType : computeSelectValue(props.currentUser.efileDocTypes),
      numberOfPages: 0,
      autoFillChecked: false,
      name : '',
      email : '',
      address1 : '',
      address2 : '',
      city: '',
      state: 'MP',
      zip: '',
      submitMessage: '',
      transactionNumber: '',
      imageId: '',
      firstPageCharge: '',
      additionalPagesCharge: '',
      surcharge: '',
      totalCharge: '',
      showCharges: prevState.showCharges,
      showConfirmationDialog: false,
      acknowledgement: false,
      primaryImage: null,
      helperImages: [],
      // don't want to reuse the key: nextHelperImageKeyIdx: 1,
      errors: {}
    }))
  }

  handleChange = (event) => {
    this.setState({
      [event.target.id]: event.target.value,
      submitMessage: '',
      errors: {}
    })
  }

  showConfirmation = () => {
    console.log("method: " + this.state.paymentMethod);

    this.setState({
      showConfirmationDialog: true
    })
  }
	
	hideConfirmation = () => {
		this.setState({
			showConfirmationDialog: false
		})
	}

  hidePaymentPage = () => {
    this.setState({
      showCreditCardPaymentDialog: false
    })
  }

  handlePay = () => {
    if (this.state.paymentMethod != PAYMENT_METHOD_CREDIT_CARD) {
      this.efileSubmit(0);
      return;
    }

    let me = this;

    (async () => {
      await
        axios.post(configData.LANDSHARK_SERVER_URL + "creditcard/efileCapture", 
          {amount: this.state.totalCharge}, { withCredentials: true })
          .then((response) => {
            this.props.handleLockout(response.data.lockout);
            let result = new AjaxResult(response);

            this.setState({
                efileSubmitPending: false,
                showCreditCardPaymentDialog: true,
                showConfirmationDialog: false,
                authAndCaptureToken: result.content().tokenResults.token
            });
          }, (error) => {
            this.setState({ loadingStatus: "" }, function() {
              AjaxResponseHandler.handleError(error, this.showAlert, this.props.history);
            }); 
          })
    })();
  }

  efileSubmit = (transactionId) => {
    if (this.showLoading() === true) {
      axios.post(configData.LANDSHARK_SERVER_URL + "efile/submission",
        {
          docType: this.state.docType,
          numberOfPages: this.state.numberOfPages,
          name: this.state.name,
          email: this.state.email,
          city: this.state.city,
          state: this.state.state,
          zip: this.state.zip,
          address1: this.state.address1,
          address2: this.state.address2,

          paymentTransactionId: transactionId,
          transactionNumber: this.state.transactionNumber,
          imageId: this.state.imageId,

          firstPageCharge: this.state.firstPageCharge,
          additionalPagesCharge: this.state.additionalPagesCharge,
          surcharge: this.state.surcharge,
          totalCharge: this.state.totalCharge,
          showCharges: this.state.showCharges
        }, { withCredentials: true })
        .then((response) => {
          this.props.handleLockout(response.data.lockout);
          let content = response.data.content;
          this.handleClear();
          this.setState(
            {
              efileSubmitPending: false, 
              loadingStatus: "",
              transactionNumber: content.transactionNumber
            }, this.showAlert("Transaction " + content.transactionNumber + " was successfully submitted.  Check your email for a confirmation."));
          this.props.updateUsageBalance(content.usageBalance);
        }, (error) => {
          this.setState({ efileSubmitPending: false, loadingStatus: "" }, 
            function() {
              AjaxResponseHandler.handleError(error, this.showAlert, null); 
            })})
    } else {
      this.setState( {efileSubmitPending: false});
    }
  }

  handleComplete = (transactionResponse) => {
    let request = transactionResponse;
    console.log(request);

    backendLogger.info("Attempting to call creditcard/efileAuthAndCaptureResponse with transactionResponse: " + JSON.stringify(request));
    axios.post(configData.LANDSHARK_SERVER_URL + "creditcard/efileAuthAndCaptureResponse", request, { withCredentials: true })
      .then((response) => {
        console.log('notify server of auth and capture response ', response);
        this.props.handleLockout(response.data.lockout);

        this.setState({
          showCreditCardPaymentDialog: false,
          paymentTransactionId: request.transId
        })
      }, (error) => {
        this.setState({ loadingStatus: "", showCreditCardPaymentDialog: false }, this.showAlert(error.message));
      })

    this.efileSubmit(transactionResponse.transId);
  }
  
  // On file select (from the pop up)
  onPrimaryImageChange = event => {
    this.setState({ primaryImage: event.target.files[0], errors: {} });
  };    

  onAutoFillChange = event => {
    event.preventDefault();
    if (this.state.autoFillChecked && this.state.autoFillChecked === true) {
      this.setState({
        autoFillChecked: false,
        name : '',
        email : '',
        address1 : '',
        address2 : '',
        city: '',
        state: 'MP',
        zip: '',
        errors: {}
      })
    } else {
      this.setState({
        autoFillChecked: true,
        name : this.state.userName,
        email : this.state.userEmail,
        address1 : this.state.userAddress,
        address2 : '',
        city: this.state.userCity,
        state: this.state.userState,
        zip: this.state.userZip,
        errors: {}
      })
    }
  };   
  
  onAcknowledgementChange = event => {
    event.preventDefault();
    if (this.state.acknowledgement && this.state.acknowledgement === true) {
      this.setState({
        acknowledgement: false,
        errors: {}
      })
    } else {
      this.setState({
        acknowledgement: true,
        errors: {}
      })
    }
  }; 

  showAlert = (alertMessage) => {
		this.setState({
		  showAlert: true,
		  alertMessage
		})
	  }
	
	hideAlert = () => {
    let me = this;

		this.setState({
			showAlert: false,
			alertMessage: ""
		}, function() {
      if (me.state.efileSubmitPending) {
        me.efileSubmit('');
      }
    })
	}

  onAddHelperImage = (event) => {
    // Add a blank row.
    let newItem = {file: null, id: this.state.nextHelperImageKeyIdx, removed: false};

    this.setState((prevState, props) => ({
      errors: {},
      helperImages: [...prevState.helperImages, newItem],
      nextHelperImageKeyIdx: prevState.nextHelperImageKeyIdx+1
    }));
  }

  onHelperImageRemove = (event) => {
    let entry = this.getHelperImage(event.target.id);

    if (!entry) return;
    
    let newEntry = { ...entry, file: null, removed: true };

    this.setState((prevState, props) => ({
      errors: {},
      helperImages: prevState.helperImages.map( (item) => {
        return item.id === entry.id ? newEntry : item;
      } )
    }));
  }

  onHelperImageChange = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    // Modify the item.  Note that we have to
    // create a copy of the array in order to
    // atomically update state.
    let entry = this.getHelperImage(event.target.id);

    if (!entry) return;

    let newEntry = { ...entry, file: file };

    this.setState((prevState, props) => ({
      errors: {},
      helperImages: prevState.helperImages.map( (item) => {
        return item.id === entry.id ? newEntry : item;
      } )
    }));
  };

  renderHelperImageColumn1 = (entry) => {
    let idString = this.getHelperImageName(entry.id);
    let id = "HELPERDIV" + idString;
    let buttonId = this.getHelperImageRemoveName(entry.id);

    return <>
      <div style={{ marginLeft: 3, display: (entry.removed ? 'none' : '') }}>
        <div style={{ fontWeight: 'bold' }}>
          <label for={id}>{translations[this.props.language].helper_document_upload}</label>
        </div>
        <input key={id} type="file" id={id} onChange={this.onHelperImageChange}
          ref={el => {
            if (el) {
              this.helperImageInputRefs[idString] = el;
            } else {
              delete this.helperImageInputRefs[idString];
            }
          }}
        ></input>
        &nbsp;<Button id={buttonId} variant="secondary" onClick={this.onHelperImageRemove}>{translations[this.props.language].helper_document_remove_button_label}</Button>
        <div style={{ color: "red", fontWeight: "bold" }}>{this.state.errors[entry.id]}</div>
      </div>
    </>;
  }

  render() {
    let eFileRef = React.createRef();
    let hasDocumentNote = this.props.countyConfigCountyEfile.documentNote !== '';
    let hasHelperDocumentNote = this.props.countyConfigCountyEfile.helperDocumentNote !== '';
    
    let formGroupRows = [];
    
    formGroupRows.push(
      {
        heading: translations[this.props.language].primary_document_heading
      });

    if (hasDocumentNote) {
      formGroupRows.push(
        {
          formGroups: [
            {
              fieldType: 'textlike',
              value: this.props.countyConfigCountyEfile.documentNote
            }
          ]
        }
      );
    }

    formGroupRows.push(
      {
        formGroups: [
          { 
            id: 'docType', 
            label: translations[this.props.language].document_type, 
            fieldType: 'select', 
            labelFor:'docType',
            primaryCriteria: true, 
            options: this.props.currentUser.efileDocTypes,
            value: this.state.docType,
            skipLinkRef: eFileRef
          }
        ]
      }
    );

      formGroupRows.push(
      {
        formGroups: [{ id: 'primaryImage', fieldType: 'file', labelFor:'primaryImage', label: translations[this.props.language].document_upload, key: this.state.fileKey, onChange: this.onPrimaryImageChange,  error: this.state.errors['primaryImage'] }]
      });

      formGroupRows.push(
      {
          heading: translations[this.props.language].helper_documents_heading
      });

    if (hasHelperDocumentNote) {
      formGroupRows.push(
        {
          formGroups: [
            {
              fieldType: 'textlike',
              value: this.props.countyConfigCountyEfile.helperDocumentNote
            }
          ]
        }
      );
   }

    const helperImageCount = this.state.helperImages.filter(item => !item.removed).length;

    if (helperImageCount < maxHelperImageCount) {
      formGroupRows.push(
        {
          formGroups: [
            {
              id: 'addHelperImage', label: translations[this.props.language].add_helper_document_button_label, fieldType: 'actionButton', onClick: this.onAddHelperImage,
              title: '', hideRow: false
            }
          ]
        });
    }

    this.state.helperImages.forEach((item, idx) => {
        formGroupRows.push({
          hideRow: item.removed,
          key: 'efileIdRow_' + item.id,
          formGroups: [
            { id: 'FGefileIdFileRow_' + item.id, fieldType: 'function', func: () => { return this.renderHelperImageColumn1(item); } }
          ]
        })
      }
    );

    formGroupRows.push(...[
      {
        heading: 'Contact',
        formGroups: [{ id: 'autofill', fieldType: 'checkbox', labelFor:'autofill', label: translations[this.props.language].use_profile, onChange: this.onAutoFillChange, checked: this.state.autoFillChecked, error: this.state.errors['audtofill'] }]
      },
      {
        formGroups: [{ id: 'name', label: translations[this.props.language].name, fieldType: 'text', labelFor:'name', value: this.state.name, required: true, error: this.state.errors['name'] },
                     { id: 'email', label: translations[this.props.language].email, fieldType: 'email', labelFor:'email', value: this.state.email, required: true, error: this.state.errors['email'] } ]
      },
      {
        formGroups: [{ id: 'address1', label: translations[this.props.language].address1, fieldType: 'text', labelFor:'address1', required: true, value: this.state.address1, error: this.state.errors['address1'] }, 
                     { id: 'address2', label: translations[this.props.language].address2, fieldType: 'text', labelFor:'address2', placeholder: '(Optional)', value: this.state.address2, error: this.state.errors['address2'] }]
      },
      {
        formGroups: [{ id: 'city', label: translations[this.props.language].city, fieldType: 'text', labelFor:'city', value: this.state.city, required: true, error: this.state.errors['city'] }, 
                     { id: 'state', label: translations[this.props.language].state, fieldType: 'select', labelFor:'state', value: this.state.state, options: usStates, required: true }, 
                     { id: 'zip', label: translations[this.props.language].zip, fieldType: 'zip', labelFor:'zip', style: {maxWidth: 80}, required: true, maxLength: 5, value: this.state.zip, error: this.state.errors['zip'] }]
      }
    ]);

     if (this.props.countyConfigCountyEfile.acknowledgement === true) {
      // When Turnkey has the new translation file, we will need to check for the Languages Config setting.  If Language Config is true, use the translation file for the acknowledgement statement.
      formGroupRows.push({
        heading: 'Acknowledgement',
        formGroups: [{ id: 'acknowledgement', fieldType: 'checkbox', label: this.props.countyConfigCountyEfile.acknowledgementStatement, onChange: this.onAcknowledgementChange, checked: this.state.acknowledgement, error: this.state.errors['acknowledgement'] }]
      });
    }
 
    const formProps = {
      componentName: componentName,
      pageHeading: translations[this.props.language].e_file,
      onChange: this.handleChange,
      onSubmit: this.handleSubmit,
      onClear: this.handleClear,
      formOnly: true,
      successIndicator: this.state.loadingStatus === LOADING_STATUES.SUCCESS,
      loadingIndicator: this.state.loadingStatus === LOADING_STATUES.LOADING,
      submitMessage: this.state.submitMessage,
      formGroupRows: formGroupRows
    }

    return <>
    <SkipLink description='Skip to Entry Form' href={'#docType'} contentRef={eFileRef} />
    <CommonTop history={this.props.history} />
    <div style={{ marginTop: 0 }}>
					<Container>
						<Row >
							<Col style={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0, marginTop: 0 }}>
								  <FormTemplate {...formProps} />
							</Col>
						</Row>
					</Container>
				</div>
    <EfileConfirmation 
      show={this.state.showConfirmationDialog} 
      cancelConfirmation={this.hideConfirmation} 
      proceedConfirmation={this.handlePay} 
      showCharges={this.state.showCharges}
      firstPageCharge={this.state.firstPageCharge} 
      additionalPagesCharge={this.state.additionalPagesCharge} 
      paymentMethod={this.state.paymentMethod}
      surcharge={this.state.surcharge}  
      totalCharge={this.state.totalCharge} 
      numberOfPages={this.state.numberOfPages} />
    <CreditCardEfilePayment
      show={this.state.showCreditCardPaymentDialog} token={this.state.authAndCaptureToken}
      cancelPayment={this.hidePaymentPage} makePayment={this.handleComplete} />
    <Alert show={this.state.showAlert} hideAlert={this.hideAlert} message={this.state.alertMessage} />
    </>
  }
}

export default connect(mapStateToProps, matchDispatchToProps)(Efile);
